const STORAGE_KEY = "marketplace-my-requests-seen-received-v1";
const VISIT_BADGES_SESSION_PREFIX = "marketplace-my-requests-visit-badges-v1";
export const MY_REQUESTS_READ_STATE_EVENT = "marketplace-my-requests-read-state";

function visitBadgesSessionKey(sub) {
  return `${VISIT_BADGES_SESSION_PREFIX}:${userKey(sub)}`;
}

function readStore() {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
}

function writeStore(store) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  window.dispatchEvent(new CustomEvent(MY_REQUESTS_READ_STATE_EVENT));
}

function userKey(sub) {
  return String(sub ?? "anonymous");
}

/**
 * @param {string|null|undefined} sub
 * @returns {Set<string>}
 */
export function getSeenReceivedOrderIds(sub) {
  if (!sub) return new Set();
  const store = readStore();
  const ids = store[userKey(sub)];
  if (!Array.isArray(ids)) return new Set();
  return new Set(ids.map((id) => String(id)));
}

/**
 * @param {string|null|undefined} sub
 * @param {Array<{ id?: string|number }>|null|undefined} receivedOrders
 * @returns {string[]}
 */
export function getUnreadReceivedOrderIds(sub, receivedOrders) {
  if (!sub || !Array.isArray(receivedOrders)) return [];
  const seen = getSeenReceivedOrderIds(sub);
  return receivedOrders
    .map((order) => String(order?.id ?? ""))
    .filter((id) => id && !seen.has(id));
}

/**
 * @param {string|null|undefined} sub
 * @param {Array<{ id?: string|number }>|null|undefined} receivedOrders
 */
export function hasUnreadReceivedOrders(sub, receivedOrders) {
  return getUnreadReceivedOrderIds(sub, receivedOrders).length > 0;
}

/**
 * @param {string|null|undefined} sub
 * @param {Iterable<string|number>} orderIds
 */
export function markReceivedOrdersSeen(sub, orderIds) {
  if (!sub) return;
  const key = userKey(sub);
  const store = readStore();
  const existing = getSeenReceivedOrderIds(sub);
  for (const rawId of orderIds) {
    const id = String(rawId ?? "");
    if (id) existing.add(id);
  }
  store[key] = [...existing];
  writeStore(store);
}

/** @param {(event: Event) => void} listener */
export function subscribeMyRequestsReadState(listener) {
  if (typeof window === "undefined") {
    return () => {};
  }
  window.addEventListener(MY_REQUESTS_READ_STATE_EVENT, listener);
  return () => window.removeEventListener(MY_REQUESTS_READ_STATE_EVENT, listener);
}

/**
 * NEW badges shown during the current My Requests visit (survives refresh, cleared on leave).
 * @param {string|null|undefined} sub
 * @returns {string[]|null} null when no active visit snapshot exists
 */
export function loadVisitNewBadgeIds(sub) {
  if (!sub || typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(visitBadgesSessionKey(sub));
    if (raw === null) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
  } catch {
    return null;
  }
}

/**
 * @param {string|null|undefined} sub
 * @param {Iterable<string|number>} orderIds
 */
export function saveVisitNewBadgeIds(sub, orderIds) {
  if (!sub || typeof window === "undefined") {
    return;
  }

  const ids = [...orderIds].map((id) => String(id)).filter(Boolean);
  window.sessionStorage.setItem(visitBadgesSessionKey(sub), JSON.stringify(ids));
}

/**
 * Clears per-visit NEW badges when leaving My Requests.
 * @param {string|null|undefined} sub
 */
export function clearVisitNewBadgeIds(sub) {
  if (!sub || typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(visitBadgesSessionKey(sub));
}
